import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets
 */

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];
  #changeHandler = this.variantChanged.bind(this);
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#syncRadioState();
    this.addEventListener('change', this.#changeHandler);
    this.#resizeObserver.observe(this);
    this.updateVariantPickerCss();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#changeHandler);
    this.#abortController?.abort();
    this.#resizeObserver.disconnect();
  }

  // Mantener una entrada por fieldset, aunque no tenga selección.
  // Reconstruir las referencias después de cada morph.
  #syncRadioState() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.#radios = fieldsets.map((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      radios.forEach((radio, inputIndex) => {
        radio.dataset.fieldsetIndex = String(fieldsetIndex);
        radio.dataset.inputIndex = String(inputIndex);
        radio.dataset.currentChecked = String(radio.checked);
        radio.dataset.previousChecked = 'false';
      });
      return radios;
    });
    this.#checkedIndices = this.#radios.map((radios) => {
      const index = radios.findIndex((radio) => radio.checked);
      return index < 0 ? [] : [index];
    });
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const selectedOption = event.target instanceof HTMLSelectElement
      ? event.target.options[event.target.selectedIndex]
      : event.target;
    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage = this.dataset.templateProductMatch === 'true'
      && !event.target.closest('product-card')
      && !event.target.closest('quick-add-dialog');
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection ? 'featured-product-information' : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;
    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }
    if (loadsNewProduct) url.pathname = newUrl;
    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => history.replaceState({}, '', url.toString()));
    }
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldset = this.querySelectorAll('fieldset')[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    if (!radios || !checkedIndices || !fieldset) return null;
    const [currentIndex, previousIndex] = checkedIndices;
    return {
      fieldset, currentIndex, previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    else if (currentIndex !== undefined) fieldset.style.removeProperty('--pill-width-current');
    if (previousWidth) fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    else if (previousIndex !== undefined) fieldset.style.removeProperty('--pill-width-previous');
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const radios = this.#radios[fieldsetIndex];
      const inputIndex = radios?.indexOf(target) ?? -1;
      if (radios && inputIndex >= 0) {
        const previousIndex = this.#checkedIndices[fieldsetIndex]?.[0];
        radios.forEach((radio, index) => {
          radio.checked = index === inputIndex;
          radio.dataset.currentChecked = String(index === inputIndex);
          radio.dataset.previousChecked = String(index === previousIndex && index !== inputIndex);
        });
        this.#checkedIndices[fieldsetIndex] = previousIndex !== undefined && previousIndex !== inputIndex
          ? [inputIndex, previousIndex] : [inputIndex];
        this.updateFieldsetCss(fieldsetIndex);
      }
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newSelectedOption = Array.from(target.options).find((option) => option.value === target.value);
      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };
    const closestSectionId = Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId));
    if (closestSectionId) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  // Limitar la selección automática a selectores Color + Talla con radios.
  // No cambiar otros grupos, desplegables ni productos de combined listings.
  #availableSizeRequest(html, requestUrl) {
    const picker = this.#responsePicker(html);
    if (!picker || picker.dataset.productId !== this.dataset.productId) return null;
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    if (fieldsets.length !== 2 || picker.querySelector('select')) return null;
    const colorName = fieldsets[0].querySelector('legend')?.childNodes[0]?.textContent?.trim() || '';
    const sizeName = fieldsets[1].querySelector('legend')?.textContent?.trim() || '';
    if (!/^(color|colour)$/i.test(colorName) || !/^(talla|size)$/i.test(sizeName)) return null;

    const url = new URL(requestUrl, window.location.origin);
    const requested = url.searchParams.get('option_values')?.split(',');
    if (!requested || requested.length !== 2) return null;
    const colors = Array.from(fieldsets[0].querySelectorAll('input[type="radio"]'));
    const selectedColor = colors.find((radio) => radio.checked || radio.hasAttribute('checked'));
    if (selectedColor?.dataset.optionValueId !== requested[0]) return null;
    const sizes = Array.from(fieldsets[1].querySelectorAll('input[type="radio"]'));
    const selectedSize = sizes.find((radio) => radio.checked || radio.hasAttribute('checked'));
    const variantText = picker.querySelector('script[type="application/json"]')?.textContent;
    const variant = variantText ? JSON.parse(variantText) : null;
    if (selectedSize?.dataset.optionAvailable === 'true' && variant?.available === true) return null;
    const firstAvailable = sizes.find((radio) => radio.dataset.optionAvailable === 'true'
      && radio.dataset.optionValueId && !radio.dataset.connectedProductUrl);
    if (!firstAvailable || firstAvailable.dataset.optionValueId === requested[1]) return null;
    url.searchParams.set('option_values', `${requested[0]},${firstAvailable.dataset.optionValueId}`);
    url.searchParams.delete('variant');
    return url.toString();
  }

  #responsePicker(html) {
    const pickers = Array.from(html.querySelectorAll('variant-picker'));
    return pickers.find((picker) => picker.dataset.blockId === this.dataset.blockId
      && picker.dataset.productId === this.dataset.productId)
      || pickers.find((picker) => picker.dataset.blockId === this.dataset.blockId)
      || pickers[0];
  }

  async fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    try {
      const load = async (url) => {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
        const responseText = await response.text();
        return new DOMParser().parseFromString(responseText, 'text/html');
      };
      let html = await load(requestUrl);
      if (morphElementSelector !== 'main') {
        const fallbackUrl = this.#availableSizeRequest(html, requestUrl);
        if (fallbackUrl) {
          // Una sola consulta adicional; nunca un bucle de cambios automáticos.
          html = await load(fallbackUrl);
        }
      }
      if (controller.signal.aborted || this.#abortController !== controller) return;
      this.#pendingRequestUrl = undefined;
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      const textContent = this.#responsePicker(html)?.querySelector('script[type="application/json"]')?.textContent;
      if (!textContent) return;
      const variant = JSON.parse(textContent);
      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const newProduct = this.updateVariantPicker(html);
        if (this.selectedOptionId) {
          this.dispatchEvent(new VariantUpdateEvent(variant, this.selectedOptionId, {
            html, productId: this.dataset.productId ?? '', newProduct,
          }));
        }
      }
      if (this.dataset.templateProductMatch === 'true'
        && !this.closest('product-card') && !this.closest('quick-add-dialog')) {
        const url = new URL(window.location.href);
        if (variant?.id) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      if (this.#abortController === controller) this.#pendingRequestUrl = undefined;
    }
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = this.#responsePicker(newHtml);
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => node instanceof HTMLElement ? node.dataset.key : undefined,
    });
    this.#syncRadioState();
    this.updateVariantPickerCss();
    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);
    for (const measurement of measurements) this.#applyFieldsetMeasurements(measurement);
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) throw new Error(`No new element source found for ${elementSelector}`);
    morph(element, newElement);
    if (this.isConnected) {
      this.#syncRadioState();
      this.updateVariantPickerCss();
    }
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
    if (this.isConnected) {
      this.#syncRadioState();
      this.updateVariantPickerCss();
    }
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}